import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Page } from '../models/page';
import { PageChangedEvent } from '../utils/events/page-changed-event';
import { ChapterChangedEvent } from '../utils/events/chapter-changed-event';
import { ViewModeChangedEvent } from '../utils/events/view-mode-changed-event';
import { EditMoveableBookFeatureEvent } from '../utils/events/edit-moveable-book-feature-event';
import { BookFeatureDataChangedEvent } from '../utils/events/book-feature-data-changed-event';
import { ZoomEvent } from '../utils/events/zoom-event';
import { Colorable } from '../models/colorable';
import { TermLookUpEvent } from '../utils/events/term-look-up-event';
import { HomeworkLink } from '../models/homework-link';

@Injectable({
    providedIn: 'root'
})
export class CommunicationService {

    private pageTurnAnnouncedSource = new Subject<PageChangedEvent>();
    pageTurnAnnounced$ = this.pageTurnAnnouncedSource.asObservable();
    private pageTurnConfirmedSource = new Subject<Array<Page>>();
    pageTurnConfirmed$ = this.pageTurnConfirmedSource.asObservable();
    private chapterTurnAnnouncedSource = new Subject<ChapterChangedEvent>();
    chapterTurnAnnounced$ = this.chapterTurnAnnouncedSource.asObservable();
    private chapterTurnConfirmedSource = new Subject<ChapterChangedEvent>();
    chapterTurnConfirmed$ = this.chapterTurnConfirmedSource.asObservable();
    private viewModeChangeAnnouncedSource = new Subject<ViewModeChangedEvent>();
    viewModeChangeAnnounced$ = this.viewModeChangeAnnouncedSource.asObservable();
    private pageSizeChangedAnnouncedSource = new Subject<number>();
    pageSizeChangedAnnounced$ = this.pageSizeChangedAnnouncedSource.asObservable();
    private editMoveableBookFeatureAnnouncedSource = new Subject<EditMoveableBookFeatureEvent>();
    editMoveableBookFeatureAnnounced$ = this.editMoveableBookFeatureAnnouncedSource.asObservable();
    private bookFeatureDataChangedAnnouncedSource = new Subject<BookFeatureDataChangedEvent>();
    bookFeatureDataChangedAnnouncedSource$ = this.bookFeatureDataChangedAnnouncedSource.asObservable();
    private zoomLevelChangedAnnouncedSource = new Subject<ZoomEvent>();
    zoomLevelChangedAnnouncedSource$ = this.zoomLevelChangedAnnouncedSource.asObservable();
    private zoomLevelChangedConfirmedSource = new Subject();
    zoomLevelChangedConfirmed$ = this.zoomLevelChangedConfirmedSource.asObservable();
    private readspeakerVisibilityToggledSource = new Subject();
    readspeakerVisibilityToggledSource$ = this.readspeakerVisibilityToggledSource.asObservable();
    private searchChangeAnnouncedSource = new Subject();
    searchChangeAnnounced$ = this.searchChangeAnnouncedSource.asObservable();
    private addHomeworkAnnouncedSource = new Subject();
    addHomeworkAnnounced$ = this.addHomeworkAnnouncedSource.asObservable();
    private clearHomeworkAnnouncedSource = new Subject();
    clearHomeworkAnnounced$ = this.clearHomeworkAnnouncedSource.asObservable();
    private homeworkChangeAnnouncedSource = new Subject();
    homeworkChangeAnnounced$ = this.homeworkChangeAnnouncedSource.asObservable();
    private homeworkSavedAnnouncedSource = new Subject();
    homeworkSavedAnnounced$ = this.homeworkSavedAnnouncedSource.asObservable();
    private homeworkRemovedAnnouncedSource = new Subject();
    homeworkRemovedAnnounced$ = this.homeworkRemovedAnnouncedSource.asObservable()
    private highlightTextboxesSource = new Subject();
    highlightTextboxesAnnounced$ = this.highlightTextboxesSource.asObservable();
    private termLookUpSource = new Subject<TermLookUpEvent>();
    termLookUpAnnounced$ = this.termLookUpSource.asObservable();
    private detectChangesSource = new Subject();
    detectChangesAnnounced$ = this.detectChangesSource.asObservable();
    private drawModeColorChangedSource = new Subject();
    drawModeColorChangedSource$ = this.drawModeColorChangedSource.asObservable();
    private drawModeBorderWidthChangedSource = new Subject();
    drawModeBorderWidthChangedSource$ = this.drawModeBorderWidthChangedSource.asObservable();
    private digiFitMenuOpenSource = new Subject();
    digiFitMenuOpenAnnounced$ = this.digiFitMenuOpenSource.asObservable();
    private quickLinkDropdownSource = new Subject();
    quickLinkDropdownOpenAnnounced$ = this.quickLinkDropdownSource.asObservable();
    private offlineVersionCreatedSource = new Subject();
    offlineVersionCreatedAnnounced$ = this.offlineVersionCreatedSource.asObservable();
    private worksheetChangeSource = new Subject();
    worksheetChangeSourceAnnounced$ = this.worksheetChangeSource.asObservable();

    openQuickLinkDropdown(quickLinkId?: number): void {
        this.quickLinkDropdownSource.next(quickLinkId);
    }

    openDigiFitMenu(pageId?: number): void {
        this.digiFitMenuOpenSource.next(pageId);
    }

    announcePageTurn(pageturnEvent: PageChangedEvent): void {
        this.pageTurnAnnouncedSource.next(pageturnEvent);
    }

    confirmPageTurn(pages: Array<Page>): void {
        this.pageTurnConfirmedSource.next(pages);
    }

    announceChapterTurn(chapterChangedEvent: ChapterChangedEvent): void {
        this.chapterTurnAnnouncedSource.next(chapterChangedEvent);
    }

    confirmChapterTurn(chapterChangedEvent: ChapterChangedEvent): void {
        this.chapterTurnConfirmedSource.next(chapterChangedEvent);
    }

    announceViewModeChange(viewModeChangeEvent: ViewModeChangedEvent): void {
        this.viewModeChangeAnnouncedSource.next(viewModeChangeEvent);
    }

    announcePageSizeChanges(): void {
        this.pageSizeChangedAnnouncedSource.next(null);
    }

    announceMoveableBookFeatureEdit(editMoveableBookFeatureEvent: EditMoveableBookFeatureEvent): void {
        this.editMoveableBookFeatureAnnouncedSource.next(editMoveableBookFeatureEvent);
    }

    announceBookFeatureDataChanged(bookFeatureDataChangedEvent: BookFeatureDataChangedEvent): void {
        this.bookFeatureDataChangedAnnouncedSource.next(bookFeatureDataChangedEvent);
    }

    announceZoomLevelChanged(zoomEvent: ZoomEvent): void {
        this.zoomLevelChangedAnnouncedSource.next(zoomEvent);
    }

    confirmZoom(): void {
        this.zoomLevelChangedConfirmedSource.next(null);
    }

    toggleReadspeakerVisibility(): void {
        this.readspeakerVisibilityToggledSource.next(null);
    }

    changeDrawModeColor(color: Colorable): void {
        this.drawModeColorChangedSource.next(color);
    }

    changeDrawModeBorderWidth(width: number): void {
        this.drawModeBorderWidthChangedSource.next(width);
    }

    announceSearchChange(): void {
        this.searchChangeAnnouncedSource.next(null);
    }

    announceAddHomework(): void {
        this.addHomeworkAnnouncedSource.next(null);
    }

    announceSavedHomework(homework: HomeworkLink): void {
        this.homeworkSavedAnnouncedSource.next(homework);
    }

    clearHomework(): void {
        this.clearHomeworkAnnouncedSource.next(null);
    }

    announceHomeworkChange(): void {
        this.homeworkChangeAnnouncedSource.next(null);
    }

    announceHighlightTextboxes(): void {
        this.highlightTextboxesSource.next(null);
    }

    announceTermLookUp(wordLookUpEvent?: TermLookUpEvent): void {
        this.termLookUpSource.next(wordLookUpEvent);
    }

    announceDetectChanges(): void {
        this.detectChangesSource.next(null);
    }

    announceOfflineVersionCreated(): void {
        this.offlineVersionCreatedSource.next(null);
    }

    announceHomeworkRemove(): void {
        this.homeworkRemovedAnnouncedSource.next(null);
    }

    announceWorksheetChange(): void {
        this.worksheetChangeSource.next(null);
    }
}
